/* eslint-disable unicorn/prefer-string-replace-all */

// https://docs.google.com/spreadsheets/d/14USYqaFoajhx5T0cVweRi7D71wrYa2XKMULBuCUFkp0/edit#gid=0

// -----------------------------------------
// 可用來驗證 Open Graph Meta Tags 資訊是否正確
// https://www.opengraph.xyz/url/https%3A%2F%2Fnews.cnyes.com%2Fnews%2Fcat%2Fheadline
//
// Facebook Debugger
// https://developers.facebook.com/tools/debug/
// -----------------------------------------

import LinkFactory from '@fe-common-utils/libs/link';
import website from '@fe-news/configs/website';
import { NewsCategory } from '@fe-news/constants/categories';
import { MagazineType } from '@fe-news/constants/mag/type';
import { PopularTopicType } from '@fe-news/constants/pop-topic/type';

const {
  newsChannelLink,
  newsCategoryChannelLink,
  mobileNewsCategoryChannelLink,
  ampNewsUrl,
  newsDetailLink,
  popTopicDetailLink,
  magazineDetailLink
} = LinkFactory;

const DEFAULT_SEO_METADATA = {
  title: '',
  keywords: [],
  description: '',
  canonical: {
    desktop: `${newsChannelLink}`,
    mobile: `${newsChannelLink}`,
    rss: `${newsChannelLink}/rss/v1/news/category/headline`
  },
  websiteUrl: `${newsChannelLink}`
};

/**
 * @param item {NavigationItem}
 * @returns {SeoMetadata}
 * @description 利用新聞分類，建立新聞分類列表的 SEO Metadata
 */
export function getSeoMetadataOfCategory(item: MaybeUndefined<NavigationItem>): SeoMetadata {
  if (!item) return DEFAULT_SEO_METADATA;

  const { title, keywords, description, category } = item;

  return {
    ...DEFAULT_SEO_METADATA,
    title: title || '',
    keywords: keywords || [],
    description: description || '',
    canonical: {
      desktop: `${newsCategoryChannelLink}/${category}`,
      mobile: `${mobileNewsCategoryChannelLink}/${category}`,
      rss: `${newsChannelLink}/rss/v1/news/category/${category}`
    }
  };
}

/**
 * @param id {string}
 * @param title {string}
 * @param summary {string}
 * @returns {SeoMetadata}
 * @description 建立新聞本文的 SEO Metadata
 */
export function getSeoMetadataOfDetail(
  news: News
): SeoMetadata & { breadcrumbs?: { categoryId: number; slug: string; name: string }[] } {
  if (!news) return DEFAULT_SEO_METADATA;

  const { newsId, title, keywords, summary, breadcrumbs, coverSrc, publishAt, author } = news;

  const category = Array.isArray(breadcrumbs) ? breadcrumbs[0]?.slug : '';
  const categoryName = Array.isArray(breadcrumbs) ? breadcrumbs.at(-1)?.name : '';
  const openGraphImageUrl = coverSrc ? `${coverSrc?.m?.src}` : `${newsChannelLink}/assets/images/og-image.png`;

  const date = new Date(publishAt * 1000);
  const publishedTime = date.toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' });

  return {
    ...DEFAULT_SEO_METADATA,
    title: `${title} | ${website.name} - ${categoryName}` || '',
    keywords: keywords || [],
    description: summary || '',
    openGraph: {
      image: openGraphImageUrl,
      publishedTime: publishedTime
    },
    category,
    canonical: {
      desktop: `${newsDetailLink}/${newsId}`,
      mobile: `${newsDetailLink}/${newsId}`
    },
    amphtml: `${ampNewsUrl}/news/id/${newsId}`,
    breadcrumbs,
    author
  };
}

/**
 * @param keyword {string}
 * @returns {SeoMetadata}
 * @description 利用搜尋關鍵字，建立新聞搜尋的 SEO Metadata
 */
export function getSeoMetadataOfSearch(keyword = ''): SeoMetadata {
  if (!keyword) return DEFAULT_SEO_METADATA;

  return {
    ...DEFAULT_SEO_METADATA,
    title: `搜尋 ${keyword} 新聞`,
    keywords: ['新聞', '搜尋'],
    description: `${keyword} 相關新聞, 報導。`,
    canonical: {
      desktop: `${newsChannelLink}/search?q=${keyword}`,
      mobile: `${newsChannelLink}/search?q=${keyword}`
    }
  };
}

/**
 * @param tag {string}
 * @returns {SeoMetadata}
 * @description 利用 tag，建立新聞 tag 的 SEO Metadata
 */
export function getSeoMetadataOfTag(tag = ''): SeoMetadata {
  if (!tag) return DEFAULT_SEO_METADATA;

  return {
    ...DEFAULT_SEO_METADATA,
    title: `#${tag} 新聞`,
    keywords: ['新聞', '標籤', '關鍵字'],
    description: `${tag}相關新聞, 報導, 資訊, 相關個股。`,
    canonical: {
      desktop: `${newsChannelLink}/tag/${tag}`,
      mobile: `${newsChannelLink}/news/tag/${tag}`
    }
  };
}

/**
 * @param item {NavigationItem}
 * @returns {SeoMetadata}
 * @description 利用新聞分類，建立新聞分類列表的 SEO Metadata
 */
export function getSeoMetadataOfPopTopic(item: MaybeUndefined<NavigationItem>): SeoMetadata {
  if (!item) return DEFAULT_SEO_METADATA;

  const { title, keywords, description, category } = item;

  return {
    ...DEFAULT_SEO_METADATA,
    title: title || '',
    keywords: keywords || [],
    description: description || '',
    canonical: {
      desktop: `${popTopicDetailLink}`,
      mobile: `${popTopicDetailLink}`,
      rss: `${newsChannelLink}/rss/v1/news/category/${category}`
    }
  };
}

/**
 * @param issue {PopTopicDataType}
 * @returns {SeoMetadata}
 * @description 建立熱門時事的 SEO Metadata
 */

const REMOVE_TAGS_AND_WHITESPACE = /<\/?[^>]+(>|$)|&nbsp;|\s+/g;

export function getSeoMetadataOfPopTopicDetail(issue: MaybeUndefined<PopularTopicType>): SeoMetadata {
  if (!issue) return DEFAULT_SEO_METADATA;

  const { title, id, content, onlineAt } = issue;

  return {
    ...DEFAULT_SEO_METADATA,
    title: `${title} | Anue鉅亨-熱門時事` || '',
    description: content ? content.replace(REMOVE_TAGS_AND_WHITESPACE, '').slice(0, 100) : '',
    canonical: {
      desktop: `${popTopicDetailLink}/${id}`,
      mobile: `${popTopicDetailLink}/${id}`
    },
    openGraph: {
      publishedTime: onlineAt
    }
  };
}

/**
 * @param issue {MagazineType}
 * @returns {SeoMetadata}
 * @description 建立雜誌內頁的 SEO Metadata
 */
export function getSeoMetadataOfMagazine(issue: MaybeUndefined<MagazineType | null>): SeoMetadata {
  if (!issue) return DEFAULT_SEO_METADATA;

  const { name, magazineId, description, image } = issue;

  return {
    ...DEFAULT_SEO_METADATA,
    title: `${name} | Anue鉅亨-雜誌` || '',
    description: description || '',
    keywords: ['鉅亨網', '雜誌', '新聞', '財經專欄', '專家觀點'],
    category: NewsCategory.MAGAZINE,
    canonical: {
      desktop: `${magazineDetailLink}/${magazineId}`,
      mobile: `${magazineDetailLink}/${magazineId}`
    },
    openGraph: {
      image: image?.m?.src || `${newsChannelLink}/assets/images/og-image.png`
    }
  };
}

/**
 * @param input {SeoMetadata}
 * @returns {Record<string, any>}
 * @description 生成網站 JSON-LD
 */

export function generateWebsiteJsonLd({
  metadata,
  type
}: {
  metadata: SeoMetadata;
  type: 'WebSite' | 'WebPage' | 'CollectionPage' | 'NewsArticle';
}): Record<string, any> {
  const jsonLd: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': type,
    name: type === 'WebSite' ? '鉅亨網' : metadata.category,
    headline: metadata.title,
    inLanguage: 'zh_TW',
    url: type === 'WebSite' ? metadata.websiteUrl : metadata.canonical?.desktop || metadata.websiteUrl,
    mainEntityOfPage: {
      '@type': type,
      '@id': type === 'WebSite' ? metadata.websiteUrl : metadata.canonical?.desktop || metadata.websiteUrl
    },
    publisher: [
      {
        '@type': 'Organization',
        name: '鉅亨網',
        url: metadata.websiteUrl,
        logo: {
          '@type': 'ImageObject',
          url: `${metadata.websiteUrl}/assets/images/og-image.png`
        }
      }
    ]
  };

  if (metadata.description !== '') {
    jsonLd.description = metadata.description;
  }

  if (metadata.openGraph?.publishedTime) {
    jsonLd.datePublished = metadata.openGraph?.publishedTime;
  }

  if (metadata.author) {
    jsonLd.publisher.push({
      '@type': 'Person',
      name: metadata.author
    });
  }

  if (metadata.openGraph?.image) {
    jsonLd.thumbnailUrl = metadata.openGraph?.image;
    jsonLd.image = {
      '@type': 'ImageObject',
      url: metadata.openGraph?.image
    };
  }

  if (metadata.keywords.length > 0) {
    jsonLd.keywords = metadata.keywords.join(',');
  }

  if (type === 'WebSite') {
    jsonLd.potentialAction = {
      '@type': 'SearchAction',
      target: `${newsChannelLink}/search?q={search_term_string}`,
      query: 'required name=search_term_string'
    };
  }

  return jsonLd;
}
